import { useCallback, useState } from 'react';
import { usePaymentGateway } from './usePaymentGateway';
import usePaymentProviders from './usePaymentProviders';
import { PAYMENT_TRANSACTION_TYPE, type PaymentTransactionType } from '../../types/enums';
import { useBalances } from '../../queries';

export const usePaymentFlowState = () => {
  const [transactionType, setTransactionType] = useState<PaymentTransactionType>(PAYMENT_TRANSACTION_TYPE.DEPOSIT);
  const [requestedMethod, setRequestedMethod] = useState<string | null>(null);

  const { paymentGateway, paymentProviders, cryptoCashier, requiresMethodBeforeForm, withdrawalEnabled } =
    usePaymentGateway();
  const filteredProviders = usePaymentProviders(paymentProviders, transactionType, paymentGateway);
  const { mainBalance } = useBalances();

  const isDeposit = transactionType === PAYMENT_TRANSACTION_TYPE.DEPOSIT;
  const isWithdrawal = transactionType === PAYMENT_TRANSACTION_TYPE.WITHDRAW;
  const hasBalance = Math.round((mainBalance?.balance ?? 0) * 100) / 100 > 0;

  // Keep the requested choice so it returns if filtering makes it eligible again.
  const eligibleRequestedMethod = filteredProviders.find((provider) => provider.value === requestedMethod)?.value;
  const defaultMethod = filteredProviders.length === 1 ? filteredProviders[0].value : null;
  const selectedMethod = eligibleRequestedMethod ?? defaultMethod;

  const showPaymentProviderPicker = requiresMethodBeforeForm && filteredProviders.length > 0;
  // Disabled crypto deposits still show an availability message through the renderer.
  const showPaymentContent = !requiresMethodBeforeForm || selectedMethod !== null;

  const balanceDisabledReason = hasBalance ? null : 'insufficient_balance';
  const withdrawalDisabledReason = withdrawalEnabled ? balanceDisabledReason : 'provider_unavailable';
  const withdrawalDisabled = withdrawalDisabledReason !== null;

  const handleTransactionTypeChange = useCallback((newType: PaymentTransactionType | null) => {
    if (newType !== null) {
      setRequestedMethod(null);
      setTransactionType(newType);
    }
  }, []);

  const handleProviderChange = useCallback((provider: string) => {
    setRequestedMethod(provider);
  }, []);

  const resetToDeposit = useCallback(() => {
    setRequestedMethod(null);
    setTransactionType(PAYMENT_TRANSACTION_TYPE.DEPOSIT);
  }, []);

  return {
    cryptoCashier,
    showPaymentProviderPicker,
    showPaymentContent,
    withdrawalDisabled,
    transactionType,
    withdrawalDisabledReason,
    selectedMethod,
    paymentGateway,
    paymentProviders,
    isDeposit,
    isWithdrawal,
    handleTransactionTypeChange,
    handleProviderChange,
    resetToDeposit,
  };
};
